import { useMemo } from 'react';
import { DataTable, DataTableBody, DataTableFooter } from '@optiaxiom/react';
import { getCoreRowModel, type PaginationState, useReactTable } from '@tanstack/react-table';

import createColumns from './columns';
import { AgentTabTableProps } from './types';

export function AgentTabTable({
  agents,
  totalCount,
  totalPages,
  page,
  pageSize,
  loading = false,
  onClickAgent,
  onCopyAgent,
  onExportAgent,
  onPublishAgent,
  onDeleteAgent,
  showPublishOption,
  onPageChange,
  onPageSizeChange,
}: AgentTabTableProps) {
  const paginationState = useMemo(
    () => ({
      pageIndex: page - 1,
      pageSize,
    }),
    [page, pageSize]
  );

  const columns = useMemo(
    () =>
      createColumns({
        onClickAgent,
        onCopyAgent,
        onExportAgent,
        onPublishAgent,
        onDeleteAgent,
        showPublishOption,
      }),
    [onClickAgent, onCopyAgent, onExportAgent, onPublishAgent, onDeleteAgent, showPublishOption]
  );

  const table = useReactTable({
    columns,
    data: agents,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: totalCount,
    pageCount: totalPages,
    state: {
      pagination: paginationState,
      columnPinning: { right: ['actions'] },
    },
    onPaginationChange: (
      updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => {
      const newPagination = typeof updater === 'function' ? updater(paginationState) : updater;

      if (newPagination.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPagination.pageIndex + 1);
      }
      if (newPagination.pageSize !== paginationState.pageSize) {
        onPageSizeChange(newPagination.pageSize);
      }
    },
  });

  return (
    <DataTable table={table}>
      <DataTableBody loading={loading} />
      <DataTableFooter showPageSizeOptions />
    </DataTable>
  );
}
